/**
 * POST /api/v1/users still validates `role` against a small legacy enum.
 * The real assignment is `roleId` (the role chosen in the UI from the roles API).
 *
 * Policy: never maintain a mapping table per custom role. If the DB code already matches
 * one of the legacy values (after stripping trailing underscores), send it; otherwise send
 * ADMIN so validation passes. Permissions follow `roleId`.
 */
const LEGACY_ROLE_ENUM = new Set([
  'ADMIN',
  'PROVINCE_ADMIN',
  'DISTRICT_ADMIN',
  'SECTOR_ADMIN',
  'CELL_ADMIN',
  'VILLAGE_ADMIN',
]);

export type CreateUserLegacyRoleCode =
  | 'ADMIN'
  | 'PROVINCE_ADMIN'
  | 'DISTRICT_ADMIN'
  | 'SECTOR_ADMIN'
  | 'CELL_ADMIN'
  | 'VILLAGE_ADMIN';

function normalizeTrailingUnderscores(code: string): string {
  return code.replace(/_+$/g, '').trim();
}

/**
 * Value for the `role` field on create-user. Custom roles (NESA, RMB_, etc.) → ADMIN automatically.
 */
export function resolveLegacyRoleForCreate(dbCode: string): CreateUserLegacyRoleCode {
  const upper = dbCode.trim().toUpperCase();
  if (LEGACY_ROLE_ENUM.has(upper)) return upper as CreateUserLegacyRoleCode;

  const normalized = normalizeTrailingUnderscores(dbCode).toUpperCase();
  if (LEGACY_ROLE_ENUM.has(normalized)) return normalized as CreateUserLegacyRoleCode;

  return 'ADMIN';
}
